import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { ISchemaFieldProps } from '../../model/IJsonSchema';
import GenericInput from '../../../../common/components/genericInput/GenericInput';
import { useTheme } from '@material-ui/core';
import { ICheckoutTheme } from '../../../../../common/theme/ICheckoutTheme';
import useCheckoutContext from '../../../../../gql/common/hooks/useCheckoutContext';
import useJourneyStepsContext from '../../../../../gql/checkout4/hooks/useJourneyStepsContext';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';
import {
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getTradeInCommonFields,
  getTradeInTextEventFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import {
  DxpDataLayerEventNames,
  DxpDataLayerType,
} from '../../../../../../../common/utilities/customDatalayerInterface';
import { TextWidgetId } from '../../../../../common/enum/TradeInComponentId';
import enquiryFormInputFieldErrorMessage from '../../service/enquiryFormInputFieldErrorMessage';

export const InputField: FunctionComponent<ISchemaFieldProps> = ({ formData }) => {
  const {
    label,
    required,
    id,
    onChange,
    value,
    rawErrors,
    options: { inputLabel },
    placeholder,
    schema: { readOnly },
    disabled,
  } = formData;

  const currentError = useMemo(() => rawErrors?.length > 0 && rawErrors[0], [rawErrors]);
  let errorMessage = enquiryFormInputFieldErrorMessage.setErrorMessage(formData, rawErrors);

  if (!errorMessage) {
    errorMessage = currentError;
  }

  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { checkoutLite } = useTheme<ICheckoutTheme>();
  const showRequiredInPlaceholder = checkoutLite?.enquiryForm?.showRequiredInPlaceholder;
  const { steps, activeStep } = useJourneyStepsContext();
  const { deal } = useCheckoutContext();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();

  const handleChange = useCallback(
    (e: React.ChangeEvent<{ value: string }>) => {
      const value = e.target.value;

      const { id, formContext, onChange } = formData;
      const { tradeIn } = formContext;
      onChange(value);

      const isTradeIn = Object.values(TextWidgetId).includes(id as TextWidgetId);

      if (deal && value && isTradeIn) {
        const data = {
          event_action: removeHostNameFromUrl(window.location.href),
          event_category: EventDataTypes.CHECKOUT_CLICK,
          event_pagecategory: EventDataTypes.CHECKOUT,
          click_url: removeHostNameFromUrl(window.location.href),
          click_name: EventDataTypes.TEXT_INPUT,
          vproduct: {
            ...vproductCommonFields(deal),
            total_weight: '',
            max_speed: '',
            est_delivery_time: deal.car?.deliveryTimeFrame?.toString() ?? '',
          },
          checkoutStep: {
            ...getCheckoutStepsCommonFields(steps, activeStep),
          },
          tradein: {
            ...getTradeInCommonFields(deal, tradeIn),
          },
        };

        const { model, mileage, registrationNumber, year, makeAutocomplete } = tradeIn;
        let additionalData: Partial<DxpDataLayerType> = {};
        let completedCondition = false;

        switch (id) {
          case TextWidgetId.TRADEIN_MODEL:
            completedCondition = !!mileage && !!registrationNumber && !!year;
            additionalData = {
              ...getTradeInTextEventFields(
                DxpDataLayerEventNames.TRADEIN_MODEL_CTA_CLICK,
                EventDataTypes.TRADEIN_MODEL,
              ),
              tradein: {
                model: value.toLowerCase(),
              },
            };
            break;
          case TextWidgetId.TRADEIN_MILEAGE:
            completedCondition = !!model && !!registrationNumber && !!year;
            additionalData = {
              ...getTradeInTextEventFields(
                DxpDataLayerEventNames.TRADEIN_MILEAGE_CTA_CLICK,
                EventDataTypes.TRADEIN_MILEAGE,
              ),
              tradein: {
                mileage: value,
              },
            };
            break;
          case TextWidgetId.TRADEIN_REGISTRATION_NUMBER:
            completedCondition = !!mileage && !!model && !!year;
            additionalData = {
              ...getTradeInTextEventFields(
                DxpDataLayerEventNames.TRADEIN_REGISTRATION_NUMBER_CTA_CLICK,
                EventDataTypes.TRADEIN_REGISTRATION_NUMBER,
              ),
              tradein: {
                registration_number: value.toLowerCase(),
              },
            };
            break;
          case TextWidgetId.TRADEIN_YEAR:
            completedCondition = !!mileage && !!registrationNumber && !!model;
            additionalData = {
              ...getTradeInTextEventFields(
                DxpDataLayerEventNames.TRADEIN_DATE_OF_REGISTRATION_CTA_CLICK,
                EventDataTypes.TRADEIN_YEAR,
              ),
              tradein: {
                date_of_registration: value,
              },
            };
            break;
          default:
            break;
        }

        callDxpDigitalAssignmentEntryEvent(
          {
            ...data,
            ...additionalData,
            tradein: {
              ...data.tradein,
              ...additionalData.tradein,
            },
          },
          jlrDataLayer,
        );

        if (value && completedCondition && !!makeAutocomplete) {
          callDxpDigitalAssignmentEntryEvent(
            {
              ...data,
              ...additionalData,
              tradein: {
                ...data.tradein,
                ...additionalData.tradein,
              },
              ...getEventDataCommonFields(DxpDataLayerEventNames.TRADEIN_DETAILS_CTA_CLICK, true),
            },
            jlrDataLayer,
          );
        }
      }
    },
    [formData, onChange, deal, steps, activeStep, jlrDataLayer, callDxpDigitalAssignmentEntryEvent],
  );

  return (
    <GenericInput
      label={label}
      required={required}
      id={id}
      value={value}
      currentError={errorMessage}
      onChange={handleChange}
      inputLabel={inputLabel}
      placeholder={placeholder}
      readOnly={readOnly}
      disabled={disabled}
      showRequiredInPlaceholder={showRequiredInPlaceholder}
    />
  );
};

export default InputField;
